import { IPatient } from '../constants/types';
import { decryptSinglePatientData } from '../utils';
import Dexie, { type EntityTable } from 'dexie';


const DB_PATIENTS_NAME = 'Patients';
const STORE_PATIENTS_NAME = 'patients';

export let db = new Dexie(DB_PATIENTS_NAME) as Dexie & {
    patients: EntityTable<IPatient, 'uuid'>;
};

// Define the database schema
db.version(2).stores({
    patients: 'uuid, uuidInvestigation, id, dateCreated, personalData.name, personalData.surnames'
});

// Version 31: Removed syncState table (simplified to localStorage)
// Keeping version 31 for compatibility, but syncState table is no longer used
db.version(31).stores({
    patients: 'uuid, uuidInvestigation, id, dateCreated, personalData.name, personalData.surnames'
}).upgrade(async tx => {
    console.log('📦 Database at v31: syncState moved to localStorage for simplicity');
});

export const deleteAllPatientsFromInvestigation = async (uuidInvestigation: string): Promise<void> => {
    await db.patients.where('uuidInvestigation').equals(uuidInvestigation).delete();
};

export const deletePatient = async (uuidPatient: string): Promise<void> => {
    await db.patients.where('uuid').equals(uuidPatient).delete();
};

export const saveListPatients = async (patients: IPatient[], investigation: any): Promise<void> => {
    console.time("Decrypting Patient Data");
    console.log("Number patients", patients.length);
    const patientsToSave = await Promise.all(patients.map(async (patient, id) => {

        // Detect the encryption scheme before decrypting: v2 records carry a nonce.
        const encScheme: 'v1' | 'v2' = Array.isArray(patient.personalData)
            && (patient.personalData as any[]).some(r => r && r.nonce)
            ? 'v2' : 'v1';

        patient.personalData = patient.personalData ? await decryptSinglePatientData(patient.personalData, investigation) : null;
        if(!patient.personalData){
            console.error("Patient without personal data", patient);
        }
        return {
            id: patient.id,
            uuid: patient.uuid,
            uuidInvestigation: investigation.uuid,
            dateCreated: new Date(patient.dateCreated),
            personalData: patient.personalData,
            encScheme
        };
    }));
    console.timeEnd("Decrypting Patient Data");
    console.time("Saving Bulk Patients");
    await db.patients.bulkPut(patientsToSave);
    console.timeEnd("Saving Bulk Patients");
}




export const getAllPatientsInvestigation = async (uuidInvestigation:string): Promise<IPatient[]> => {
    return await db.patients
        .where('uuidInvestigation')
        .equals(uuidInvestigation)
        .sortBy('dateCreated')
  };
// Save data to IndexedDB
export const savePatient = async (patientData: IPatient, uuidInvestigation: string): Promise<void> => {
    await db.patients.put({
        id: patientData.id,
        uuid: patientData.uuid,
        uuidInvestigation: uuidInvestigation,
        dateCreated: patientData.dateCreated,
        personalData: patientData.personalData
    });
};

// Type for patient fetch function
export type FetchPatientFn = (uuidPatient: string) => Promise<IPatient | null>;

// Fetch data from IndexedDB - this is the default/real implementation
export const fetchPatient: FetchPatientFn = async (uuidPatient: string): Promise<IPatient | null> => {
    const patient = await db.patients.get(uuidPatient);
    return patient ? patient : null;
};

export const updatePatientPersonalData = async (uuidPatient: string, updatedPersonalData: Partial<IPatient>): Promise<void> => {
    const existingPatient = await db.patients.get(uuidPatient);
    if (!existingPatient) {
        throw new Error(`Patient with uuid ${uuidPatient} not found`);
    }
    
    const updatedPatient = {
        ...existingPatient,
        personalData: { ...existingPatient.personalData, ...updatedPersonalData },
        uuid: uuidPatient // Ensure uuid doesn't get overwritten
    };

    await db.patients.put(updatedPatient);
};

export const findPatientByUUIDAndInvestigation = async (uuid: string, uuidInvestigation: string): Promise<IPatient | null> => {
    const patient = await db.patients
        .where('uuidInvestigation')
        .equals(uuidInvestigation)
        .and(patient => patient.uuid === uuid)
        .first();
    return patient ? patient : null;
};

export const findPatientsByNameOrSurname = async (nameOrSurname: string, uuidInvestigation: string): Promise<IPatient[]> => {
    const searchTerms = nameOrSurname.toLowerCase().split(' ');
    return await db.patients
        .where('uuidInvestigation').equals(uuidInvestigation)
        .and(patient => {
            const fullName = `${patient.personalData.name} ${patient.personalData.surnames}`.toLowerCase();
            const matchhealthId = patient.personalData.automated_health_id ? patient.personalData.automated_health_id.toLowerCase().includes(nameOrSurname.toLowerCase()) : false;
            return searchTerms.every(term => fullName.includes(term)) || matchhealthId;
        })
        .toArray();
};

export async function clearPatientsStore(): Promise<void> {
    await db.patients.clear();
    // Clear event-based sync state from localStorage
    // Note: Sync functions now live in src/utils/index.jsx
    localStorage.removeItem('lastEventId');
    localStorage.removeItem('lastSyncTimestamp');
    localStorage.removeItem('lastSyncedPatientCount');
    localStorage.removeItem("lastUpdatePatients"); // Legacy timestamp-based sync
    localStorage.removeItem("patient_sync_state"); // Per-investigation event-based sync state
    console.log('🗑️ Cleared all patient data and sync state');
}
